/**
 * Constantes do Sistema de Templates de Tenant.
 *
 * Centraliza versões, chaves de módulos e estados usados no formato
 * `.uaizytemplate` e nos adaptadores de importação/exportação.
 *
 * @version 1.0.0
 */

/** Versão atual do schema do pacote de template. */
export const TEMPLATE_PACKAGE_SCHEMA_VERSION = '1.0.0';

/** Extensão de arquivo usada para pacotes exportados. */
export const TEMPLATE_PACKAGE_EXTENSION = '.uaizytemplate';

/** Tamanho máximo de um pacote em bytes (50 MB). */
export const TEMPLATE_PACKAGE_MAX_SIZE_BYTES = 50 * 1024 * 1024;

/** Chaves dos módulos disponíveis para exportação/importação. */
export const TEMPLATE_MODULE_KEYS = [
  'crm',
  'contracts',
  'scheduling',
  'chat',
  'tasks',
  'flows',
  'workflow',
  'reminders',
  'dashboards',
  'referral',
  'vehicles',
  'permissions',
] as const;

/** Tipo derivado das chaves de módulo. */
export type TemplateModuleKey = (typeof TEMPLATE_MODULE_KEYS)[number];

/** Status possíveis de um pacote/versão. */
export const TEMPLATE_PACKAGE_STATUSES = [
  'draft',
  'valid',
  'invalid',
  'applied',
] as const;

/** Status possíveis de um job de template. */
export const TEMPLATE_JOB_STATUSES = [
  'pending',
  'running',
  'paused',
  'completed',
  'failed',
  'compensating',
  'compensated',
] as const;

/** Tipos de job de template. */
export const TEMPLATE_JOB_KINDS = [
  'export',
  'import',
  'apply',
  'rollback',
] as const;

/** Status possíveis de um plano (dry-run). */
export const TEMPLATE_PLAN_STATUSES = [
  'pending',
  'approved',
  'expired',
  'applied',
  'failed',
] as const;

/** Status possíveis de um passo de aplicação. */
export const TEMPLATE_STEP_STATUSES = [
  'pending',
  'running',
  'completed',
  'failed',
  'compensating',
  'compensated',
  'skipped',
] as const;

/** Métodos de resolução de referências. */
export const TEMPLATE_REFERENCE_RESOLUTION_METHODS = [
  'manual',
  'stable_key',
  'exact_match',
  'fuzzy_suggestion',
] as const;

/** Ações de permissão específicas do módulo de templates. */
export const TEMPLATE_PERMISSION_ACTIONS = [
  'view_library',
  'export_template',
  'import_simulate',
  'apply_template',
  'rollback_template',
  'delete_archive_template',
] as const;

// =============================================================================
// Tipos derivados das listas acima (usados nos contratos TypeScript)
// =============================================================================

/** Situação de um pacote/versão. */
export type TemplatePackageStatus = (typeof TEMPLATE_PACKAGE_STATUSES)[number];
/** Situação de uma execução. */
export type TemplateJobStatus = (typeof TEMPLATE_JOB_STATUSES)[number];
/** Tipo de execução. */
export type TemplateJobKind = (typeof TEMPLATE_JOB_KINDS)[number];
/** Situação de um plano (simulação). */
export type TemplatePlanStatus = (typeof TEMPLATE_PLAN_STATUSES)[number];
/** Situação de um passo de aplicação. */
export type TemplateStepStatus = (typeof TEMPLATE_STEP_STATUSES)[number];
/** Método de resolução de referência. */
export type TemplateReferenceResolutionMethod =
  (typeof TEMPLATE_REFERENCE_RESOLUTION_METHODS)[number];
/** Ação de permissão do módulo de templates. */
export type TemplatePermissionAction = (typeof TEMPLATE_PERMISSION_ACTIONS)[number];
